import React from 'react';
import RoomSheet from './RoomSheet';
import {
  isBookOpen, pageLine, readingNowWords, shelfModel, spineLook, spineAuthor, libbyHref,
} from '../../utils/houseLibrary';

/**
 * 📚 LibraryShelf: the House library's shelf (HOUSE_LIBRARY_SPEC_2026_09_30.md
 * §3.2; Nina's pick 09-30: "the Shelf as drawn", minus the Odd shelf's
 * "Tap one…" line). A RoomSheet: a dialog on desktop, a bottom sheet on phones.
 * Rows, top to bottom: Reading now · 🍂 October (only in October, house time;
 * other months those books sit on The shelf) · The shelf · Newer books ·
 * Open in Libby · Odd shelf · the credit. Text spines, no cover art; a ribbon on
 * a book the house left a page in. A spine opens that book for the room (the
 * page's openBook → bookControl). Libby links open Libby in a new tab. An Odd
 * shelf title opens ONE silly page in a small card, never the reader.
 * Theme armor: classes are `hlb-` (no card/panel/chip/message/btn/badge/tag/
 * user/player/header/author/surface/input/button/primary inside), buttons carry
 * data-prop-ui + doubled-class !important rules, links too. Every padded box
 * sets box-sizing (no global reset here). Player copy: no em dashes.
 * 📚 10-02 sharp spines (Nina: "the shelf blurry letters"): a browser that has
 * `sideways-lr` draws the same bottom-to-top spine text with NO transform (a
 * rotated run of text rasterizes soft), and the shelf opens without the
 * sheet's scale pop. Older browsers keep the rotate.
 */
export const SHELF_CSS = `
.rsh-scrim[data-phone="0"] .rsh-box[data-testid="library-shelf"] { width: min(980px, 100%); }
.rsh-box[data-testid="library-shelf"] { background: #1f1813; color: #f3e9d8; box-shadow: inset 0 0 0 1px #4d3b2e, 0 18px 60px rgba(0,0,0,0.6); }
.rsh-box[data-testid="library-shelf"] .rsh-ttl.rsh-ttl.rsh-ttl { font: 600 20px/1.2 Literata, Georgia, "Times New Roman", serif !important; color: #f3e9d8 !important; }
.rsh-box[data-testid="library-shelf"] .rsh-x.rsh-x.rsh-x { color: #8a7a67 !important; }
.rsh-box[data-testid="library-shelf"] .rsh-x.rsh-x.rsh-x:hover { background: #2a2019 !important; color: #f3e9d8 !important; }
.rsh-box[data-testid="library-shelf"] .rsh-grab { background: #4d3b2e; }
.hlb-sh, .hlb-sh * { box-sizing: border-box; }
.hlb-sh { display: grid; gap: 18px; padding-bottom: 6px; font: 400 14px/1.45 ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif; color: #f3e9d8; text-shadow: none; }
.hlb-shrow { display: grid; gap: 8px; min-width: 0; }
.hlb-rt { display: flex; align-items: baseline; flex-wrap: wrap; gap: 4px 10px; font: 700 12px/1.2 ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif; letter-spacing: .09em; text-transform: uppercase; color: #b8a791; }
.hlb-rt span { letter-spacing: 0; text-transform: none; font-weight: 400; color: #8a7a67; font-size: 12.5px; }
.hlb-shnote { padding: 9px 12px; border-radius: 10px; background: #2a2019; border: 1px solid #a8773a; color: #f2b35e; font-size: 13.5px; }
.hlb-now { display: flex; flex-wrap: wrap; align-items: center; gap: 12px; padding: 12px 14px; border-radius: 12px; background: #2a2019; border: 1px solid #4d3b2e; }
.hlb-nw { flex: 1 1 220px; min-width: 0; }
.hlb-nt { font: 600 16px/1.25 Literata, Georgia, "Times New Roman", serif; color: #f3e9d8; }
.hlb-nm { color: #b8a791; font-size: 13px; margin-top: 2px; }
.hlb-sh .hlb-act.hlb-act { all: unset; box-sizing: border-box; display: inline-flex; align-items: center; justify-content: center; margin-left: auto !important; height: 36px; padding: 0 16px !important; border-radius: 9px !important; border: 1px solid #f2b35e !important; background: #f2b35e !important; color: #1b140f !important; font: 700 13.5px/1 ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif !important; cursor: pointer; box-shadow: none !important; text-shadow: none !important; transform: none !important; position: static !important; overflow: visible !important; min-width: 0 !important; white-space: nowrap; }
.hlb-sh .hlb-act.hlb-act:hover { filter: brightness(1.06); transform: none !important; box-shadow: none !important; }
.hlb-sh .hlb-act.hlb-dim.hlb-dim { background: #33271e !important; border-color: #4d3b2e !important; color: #f3e9d8 !important; margin-left: 0 !important; }
.hlb-spines { display: flex; gap: 6px; overflow-x: auto; overflow-y: hidden; padding: 6px 2px 10px; align-items: flex-end; scrollbar-width: thin; }
.hlb-sh .hlb-spn.hlb-spn { all: unset; box-sizing: border-box; flex: 0 0 auto; min-width: 40px; border-radius: 4px 4px 2px 2px !important; position: relative !important; overflow: visible !important; display: flex; flex-direction: row-reverse; justify-content: center; align-items: flex-end; gap: 3px; padding: 14px 7px 10px !important; margin: 0 !important; border: 0 !important; box-shadow: inset -3px 0 0 rgba(0,0,0,.18), inset 3px 0 0 rgba(255,255,255,.08) !important; cursor: pointer; text-shadow: none !important; transform: none !important; transition: transform .15s ease; }
.hlb-sh .hlb-spn.hlb-spn:hover, .hlb-sh .hlb-spn.hlb-spn:focus-visible { transform: translateY(-4px) !important; box-shadow: inset -3px 0 0 rgba(0,0,0,.18), inset 3px 0 0 rgba(255,255,255,.08), 0 6px 14px rgba(0,0,0,.4) !important; }
.hlb-sh .hlb-spn.hlb-spn:focus-visible { outline: 2px solid #f2b35e; outline-offset: 2px; }
.hlb-sh .hlb-spn.hlb-spn[data-busy="1"] { opacity: .55; cursor: wait; }
.hlb-sh .hlb-spn.hlb-spn[data-open="1"] { box-shadow: inset -3px 0 0 rgba(0,0,0,.18), inset 3px 0 0 rgba(255,255,255,.08), 0 0 0 2px #f2b35e !important; }
.hlb-st { writing-mode: vertical-rl; transform: rotate(180deg); font: 600 12px/1.15 Literata, Georgia, serif; color: #fbf3e3; max-height: 100%; text-align: left; }
.hlb-sa { writing-mode: vertical-rl; transform: rotate(180deg); font: 600 8px/1 ui-sans-serif, system-ui, sans-serif; color: rgba(251,243,227,.68); letter-spacing: .08em; white-space: nowrap; }
@supports (writing-mode: sideways-lr) { .hlb-st, .hlb-sa { writing-mode: sideways-lr; transform: none; } }
@media (prefers-reduced-motion: no-preference) { .rsh-scrim[data-phone="0"] .rsh-box[data-testid="library-shelf"] { animation-name: hlbShelfIn; } }
@keyframes hlbShelfIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
.hlb-rib { position: absolute; top: -2px; right: 5px; width: 9px; height: 26px; background: #f2b35e; clip-path: polygon(0 0, 100% 0, 100% 100%, 50% 78%, 0 100%); }
.hlb-ribtxt { font: 600 10.5px/1.2 ui-sans-serif, system-ui, sans-serif; color: #f2b35e; }
.hlb-libby { display: grid; grid-template-columns: repeat(auto-fill, minmax(210px, 1fr)); gap: 8px; }
.hlb-lb { display: grid; grid-template-columns: 8px minmax(0, 1fr); gap: 10px; align-items: center; padding: 9px 11px; border: 1px solid #3b2d23; border-radius: 10px; background: #1a1411; min-width: 0; }
.hlb-sw { height: 40px; border-radius: 2px; }
.hlb-lt { font: 600 13.5px/1.25 Literata, Georgia, serif; color: #f3e9d8; }
.hlb-la { font-size: 12px; color: #8a7a67; }
.hlb-go { display: flex; gap: 12px; margin-top: 4px; align-items: center; }
.hlb-sh a.hlb-ln.hlb-ln { color: #f2b35e !important; font: 600 12px/1.2 ui-sans-serif, system-ui, sans-serif !important; text-decoration: none !important; display: inline-flex; align-items: center; min-height: 24px; }
.hlb-sh a.hlb-ln.hlb-ln:hover { text-decoration: underline !important; color: #ffcf8a !important; }
.hlb-sh a.hlb-ln.hlb-au.hlb-au { color: #b8a791 !important; }
.hlb-odd { display: flex; flex-wrap: wrap; gap: 8px; }
.hlb-sh .hlb-oddb.hlb-oddb { all: unset; box-sizing: border-box; font: italic 13.5px/1.3 Literata, Georgia, serif !important; color: #dccfb9 !important; border: 1px dashed #4d3b2e !important; border-radius: 8px !important; padding: 7px 11px !important; margin: 0 !important; background: #1a1411 !important; cursor: pointer; text-shadow: none !important; box-shadow: none !important; transform: none !important; position: static !important; overflow: visible !important; min-width: 0 !important; }
.hlb-sh .hlb-oddb.hlb-oddb:hover, .hlb-sh .hlb-oddb.hlb-oddb[aria-pressed="true"] { border-color: #a8773a !important; color: #f3e9d8 !important; transform: none !important; box-shadow: none !important; }
.hlb-sh .hlb-oddb.hlb-oddb:focus-visible, .hlb-sh .hlb-act.hlb-act:focus-visible { outline: 2px solid #f2b35e; outline-offset: 2px; }
.hlb-oddc { position: relative; padding: 12px 40px 12px 14px; border-radius: 10px; background: #f3e8d0; color: #2b2118; font: 400 14.5px/1.55 Literata, Georgia, serif; box-shadow: 0 6px 18px rgba(0,0,0,.35); }
.hlb-oddc i { display: block; font-weight: 600; margin-bottom: 4px; }
.hlb-sh .hlb-oddx.hlb-oddx { all: unset; box-sizing: border-box; position: absolute !important; top: 6px; right: 6px; width: 30px; height: 30px; display: inline-grid; place-items: center; border-radius: 8px !important; color: #7a6a55 !important; background: transparent !important; border: 0 !important; font: 600 14px/1 ui-sans-serif, system-ui, sans-serif !important; cursor: pointer; text-shadow: none !important; box-shadow: none !important; transform: none !important; overflow: visible !important; }
.hlb-credit { margin: 0; font-size: 12.5px; color: #8a7a67; border-top: 1px solid #3b2d23; padding-top: 12px; }
.hlb-wait2 { color: #b8a791; font-size: 13.5px; }
@media (prefers-reduced-motion: reduce) { .hlb-sh .hlb-spn.hlb-spn { transition: none; } }
`;

function Spine({ book, mark, busy, isOpen, onOpen }) {
  const look = spineLook(book.slug);
  const saved = Number.isInteger(mark) && mark > 1;
  const label = `${book.title} by ${book.author || 'unknown'}${saved ? `, the house left off at p. ${mark}` : ''}`;
  return (
    <button type="button" className="hlb-spn" data-prop-ui="" data-shelf-spine={book.slug}
      data-busy={busy ? '1' : undefined} data-open={isOpen ? '1' : undefined}
      aria-label={label} title={saved ? `${book.title} · p. ${mark}` : book.title}
      onClick={() => { if (!busy && onOpen) onOpen(book.slug); }}
      style={{ background: look.bg, height: look.h, '--prop-bg': look.bg }}>
      {saved ? <span className="hlb-rib" aria-hidden="true" data-shelf-ribbon={mark} /> : null}
      <span className="hlb-st" aria-hidden="true">{book.title}</span>
      <span className="hlb-sa" aria-hidden="true">{spineAuthor(book.author)}</span>
    </button>
  );
}

export default function LibraryShelf({
  open, onClose, isMobile = false, manifest = null, shelfLoad = 'idle', onRetry = null,
  book = null, me = null, serverNow = Date.now(), here = false, alongLine = '',
  note = null, oddPick = null, onOddPick = null, busySlug = null, onOpenBook = null, onJoin = null,
}) {
  if (!open) return null;
  const m = manifest ? shelfModel(manifest, { now: serverNow }) : null;
  const marks = (book && book.bookmarks) || {};
  const reading = isBookOpen(book);
  const total = m ? m.october.length + m.classics.length : 0;
  const anyRibbon = m ? [...m.october, ...m.classics].some((b) => Number(marks[b.slug]) > 1) : false;
  const odd = m && oddPick ? m.odd.find((o) => o.title === oddPick) : null;
  const spines = (list) => list.map((b) => (
    <Spine key={b.slug} book={b} mark={Number(marks[b.slug]) || null}
      busy={busySlug === b.slug} isOpen={reading && book.book === b.slug} onOpen={onOpenBook} />
  ));
  const meta = reading ? [
    book.author,
    pageLine(book.page, book.pages),
    readingNowWords(book, me, serverNow), // "Book club" while the club reads and nobody holds the bookmark
    alongLine,
  ].filter(Boolean).join(' · ') : '';
  return (
    <RoomSheet open={open} onClose={onClose} title="The shelf" isMobile={isMobile} accent="#f2b35e" testId="library-shelf">
      <style>{SHELF_CSS}</style>
      <div className="hlb-sh">
        {note ? <div className="hlb-shnote" role="status" data-shelf-note="1">{note}</div> : null}
        {reading ? (
          <section className="hlb-shrow" data-shelf-row="now">
            <div className="hlb-rt">Reading now</div>
            <div className="hlb-now">
              <div className="hlb-nw">
                <div className="hlb-nt">{book.title}</div>
                <div className="hlb-nm">{meta}</div>
              </div>
              <button type="button" className="hlb-act" data-prop-ui="" data-shelf-join={here ? 'read' : 'join'}
                onClick={() => { if (onJoin) onJoin(); }}>{here ? 'Read' : 'Join'}</button>
            </div>
          </section>
        ) : null}
        {!m ? (
          <section className="hlb-shrow" data-shelf-row="loading">
            {shelfLoad === 'error'
              ? <div className="hlb-wait2">The shelf is stuck. <button type="button" className="hlb-act hlb-dim" data-prop-ui="" onClick={() => { if (onRetry) onRetry(); }}>Try again</button></div>
              : <div className="hlb-wait2">Pulling books off the shelf…</div>}
          </section>
        ) : (<>
          {m.october.length ? (
            <section className="hlb-shrow" data-shelf-row="october">
              <div className="hlb-rt">{m.labels.october.label} <span>for the season</span></div>
              <div className="hlb-spines">{spines(m.october)}</div>
            </section>
          ) : null}
          {m.classics.length ? (
            <section className="hlb-shrow" data-shelf-row="classics">
              <div className="hlb-rt">{m.labels.classics.label} <span>{total} classics, free to read here</span></div>
              <div className="hlb-spines">{spines(m.classics)}</div>
              {anyRibbon ? <div className="hlb-ribtxt">🎗 a ribbon = the house's page is saved</div> : null}
            </section>
          ) : null}
          {m.libby.length ? (
            <section className="hlb-shrow" data-shelf-row="libby">
              <div className="hlb-rt">{m.labels.libby.label} · Open in Libby <span>borrow with your own library card</span></div>
              <div className="hlb-libby">
                {m.libby.map((b) => {
                  const ebook = libbyHref(b.share_url);
                  const audio = libbyHref(b.audio_share_url);
                  return (
                    <div className="hlb-lb" key={`${b.title}|${b.ebook_id}`} data-shelf-libby={b.ebook_id}>
                      <div className="hlb-sw" style={{ background: spineLook(b.title).bg }} aria-hidden="true" />
                      <div style={{ minWidth: 0 }}>
                        <div className="hlb-lt">{b.title}</div>
                        <div className="hlb-la">{b.author}</div>
                        <div className="hlb-go">
                          {ebook ? <a className="hlb-ln" href={ebook} target="_blank" rel="noopener noreferrer" aria-label={`Open ${b.title} in Libby (new tab)`}>Open in Libby ↗</a> : null}
                          {audio ? <a className="hlb-ln hlb-au" href={audio} target="_blank" rel="noopener noreferrer" aria-label={`${b.title}, the audiobook, in Libby (new tab)`} title="The audiobook">🎧</a> : null}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          ) : null}
          {m.odd.length ? (
            <section className="hlb-shrow" data-shelf-row="odd">
              <div className="hlb-rt">{m.labels.odd.label} <span>{String(m.labels.odd.sub || '').replace(/^./, (c) => c.toLowerCase())}</span></div>
              <div className="hlb-odd">
                {m.odd.map((o) => (
                  <button type="button" key={o.title} className="hlb-oddb" data-prop-ui="" data-shelf-odd={o.title}
                    aria-pressed={oddPick === o.title ? 'true' : 'false'}
                    onClick={() => { if (onOddPick) onOddPick(oddPick === o.title ? null : o.title); }}>{o.title}</button>
                ))}
              </div>
              {odd ? (
                <div className="hlb-oddc" data-shelf-odd-page="1" role="note">
                  <i>{odd.title}</i>
                  {odd.page}
                  <button type="button" className="hlb-oddx" data-prop-ui="" aria-label="Put it back" onClick={() => { if (onOddPick) onOddPick(null); }}>✕</button>
                </div>
              ) : null}
            </section>
          ) : null}
          {m.credit ? <p className="hlb-credit" data-shelf-credit="1">{m.credit}</p> : null}
        </>)}
      </div>
    </RoomSheet>
  );
}
